import { DEFAULT_PAGE_SIZE } from 'configs/AppConfig'
import { organizationKey } from 'constants/RoutesKeys'
import { getPageBasePath, getPageSegment } from 'constants/PageRoutes'

/** UI label overrides. Key = API `organizationType` value. */
const ORGANIZATION_TYPE_LABELS = {
  'Elder Forum': 'Elders Forum',
}

/** Extra slugs that resolve to an API type (e.g. display label slug). */
const ORGANIZATION_TYPE_SLUG_ALIASES = {
  'elders-forum': 'Elder Forum',
}

export const toOrganizationTypeSlug = (type) =>
  String(type || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

/** Display name for nav / page titles. API value unchanged. */
export const getOrganizationTypeLabel = (organizationType) => {
  const type = String(organizationType || '').trim()
  if (!type) return ''
  return ORGANIZATION_TYPE_LABELS[type] || type
}

export const resolveOrganizationTypeFromSlug = (slug, types = []) => {
  const normalized = toOrganizationTypeSlug(slug)
  if (!normalized) return null

  if (ORGANIZATION_TYPE_SLUG_ALIASES[normalized]) {
    const aliased = ORGANIZATION_TYPE_SLUG_ALIASES[normalized]
    if (!(types || []).length || (types || []).includes(aliased)) {
      return aliased
    }
  }

  const match = (types || []).find(
    (type) => toOrganizationTypeSlug(type) === normalized,
  )
  if (match) return match

  // Match display-label slug → API type (e.g. elders-forum → Elder Forum)
  const byLabel = (types || []).find(
    (type) => toOrganizationTypeSlug(getOrganizationTypeLabel(type)) === normalized,
  )
  if (byLabel) return byLabel

  try {
    const decoded = decodeURIComponent(String(slug || ''))
    const byDecoded = (types || []).find(
      (type) => toOrganizationTypeSlug(type) === toOrganizationTypeSlug(decoded),
    )
    if (byDecoded) return byDecoded
  } catch {
    // ignore decode errors
  }

  if ((types || []).length > 0) {
    return null
  }

  return normalized
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export const getOrganizationTypedBasePath = (typeOrSlug) => {
  const slug = toOrganizationTypeSlug(typeOrSlug)
  return `${getPageBasePath(organizationKey)}/${slug}`
}

export const getOrganizationTypedListPath = (typeOrSlug) =>
  `${getOrganizationTypedBasePath(typeOrSlug)}/${getPageSegment(organizationKey, 'list')}`

export const getOrganizationTypedListPathWithPagination = (
  typeOrSlug,
  pageSize = DEFAULT_PAGE_SIZE,
) => `${getOrganizationTypedListPath(typeOrSlug)}?page=1&limit=${pageSize}`

export const getOrganizationTypedAddPath = (typeOrSlug) =>
  `${getOrganizationTypedBasePath(typeOrSlug)}/${getPageSegment(organizationKey, 'add')}`

export const getOrganizationTypedEditBasePath = (typeOrSlug) =>
  `${getOrganizationTypedBasePath(typeOrSlug)}/${getPageSegment(organizationKey, 'edit')}`

export const getOrganizationTypedEditPath = (typeOrSlug, id) =>
  `${getOrganizationTypedEditBasePath(typeOrSlug)}/${id}`

export const getOrganizationTypedListRoute = () =>
  `:organizationTypeSlug/${getPageSegment(organizationKey, 'list')}`

export const getOrganizationTypedAddRoute = () =>
  `:organizationTypeSlug/${getPageSegment(organizationKey, 'add')}`

export const getOrganizationTypedEditRoute = () =>
  `:organizationTypeSlug/${getPageSegment(organizationKey, 'edit')}/:id`

export const getOrganizationTypedListNavigateTo = (
  typeOrSlug,
  pageSize = DEFAULT_PAGE_SIZE,
) =>
  `${toOrganizationTypeSlug(typeOrSlug)}/${getPageSegment(organizationKey, 'list')}?page=1&limit=${pageSize}`

/** Org types: max one record on listing (Add hide after first; Delete hide). */
const RESTRICTED_LIST_ORGANIZATION_TYPES = [
  'altar boys',
  'alter boys',
  'vanitha samajam',
  'youth association',
  'church choir',
  'elder forum',
  'elders forum',
  'website committee',
]

export const isOrganizationTypeListRestricted = (organizationType) => {
  const normalized = String(organizationType || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
  if (!normalized) return false
  return RESTRICTED_LIST_ORGANIZATION_TYPES.includes(normalized)
}

/** @deprecated use isOrganizationTypeListRestricted */
export const shouldHideOrganizationAddButton = isOrganizationTypeListRestricted
